import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { LiquidacionService } from 'src/app/services/liquidacion/liquidacion.service';
import { MESES, semanasDe, CeldaMes } from 'src/app/utils/calendario-mes';

/**
 * EL MES EN CUADRÍCULA (14.4, LQV-09): en la pestaña Cierre, cada día del mes con su estado (cerrado, abierto, sin cierre,
 * no hábil o feriado) y su neto (Bs grande, USD pequeño; sin tasa, USD con aviso: BSP-03). Una sola consulta al núcleo por
 * mes (LQV-10). Tocar un día con cierre lo abre (emite `abrir`); se navega mes a mes. Es opcional (Mario, 24-oct: «que sea
 * opcinal verlo… porque quita espacio»): la pestaña Cierre lo monta solo si se pide y lo quita con `cerrar`.
 * No confundir con `app-liquidacion-calendario`, el calendario de liquidez de Consultas.
 */
@Component({
  selector: 'app-liquidacion-calendario-mes',
  templateUrl: './liquidacion-calendario-mes.component.html'
})
export class LiquidacionCalendarioMesComponent implements OnChanges {
  /** El día que se está viendo en la pestaña Cierre (AAAA-MM-DD); si cambia de mes, el calendario lo sigue. */
  @Input() dia = '';
  @Output() abrir = new EventEmitter<string>();
  @Output() cerrar = new EventEmitter<void>();

  mes = '';                 // AAAA-MM del mes que se ve
  data: any = null;
  cargando = false;
  error = '';

  constructor(private liq: LiquidacionService) { }

  ngOnChanges(): void {
    const m = /^\d{4}-\d{2}/.test(this.dia) ? this.dia.slice(0, 7) : '';
    if (m && m !== this.mes) { this.mes = m; this.data = null; this.cargar(); }
  }

  cargar = async (): Promise<void> => {
    if (!this.mes) return;
    const pedido = this.mes;
    this.cargando = true; this.error = '';
    try {
      const r = await this.liq.cierresDelMes(pedido);
      if (pedido === this.mes) this.data = r;   // si ya se pasó a otro mes, esta respuesta llegó tarde
    } catch (e: any) {
      if (pedido === this.mes) { this.data = null; this.error = e?.error?.msg || 'No se pudo leer el calendario del mes.'; }
    } finally { if (pedido === this.mes) this.cargando = false; }
  };

  mover = (n: number): void => {
    const [a, m] = this.mes.split('-').map(Number);
    const f = new Date(Date.UTC(a, m - 1 + n, 1, 12));
    this.mes = f.toISOString().slice(0, 7);
    this.cargar();
  };

  /** El motivo del núcleo sin su punto final: la frase ya cierra con uno. */
  motivo = (m: string): string => String(m || '').replace(/\.+\s*$/, '');

  get titulo(): string { return this.mes ? `${MESES[+this.mes.slice(5, 7) - 1]} ${this.mes.slice(0, 4)}` : ''; }

  /** Las semanas de lunes a domingo; cada celda trae el día del núcleo (o null si es de otro mes). */
  get semanas(): { celda: CeldaMes; d: any }[][] {
    if (!this.mes) return [];
    const porDia = new Map<string, any>((this.data && this.data.dias || []).map((x: any) => [x.dia, x]));
    const hoy = this.data && this.data.hoy ? this.data.hoy.dia : '';
    return semanasDe(+this.mes.slice(0, 4), +this.mes.slice(5, 7) - 1, hoy).map((s) => s.map((celda) => ({ celda, d: celda.delMes ? (porDia.get(celda.dia) || null) : null })));
  }

  /** Solo se abre un día con cierre (cerrado o abierto) o el que el núcleo dice que sigue por abrir. */
  puedeAbrir = (d: any): boolean => !!d && (d.estado === 'CERRADO' || d.estado === 'ABIERTO' || (!!this.data?.siguiente_a_abrir && this.data.siguiente_a_abrir.dia === d.dia && !!this.data.siguiente_a_abrir.se_puede));
  tocar = (d: any): void => { if (this.puedeAbrir(d)) this.abrir.emit(d.dia); };

  textoEstado = (d: any): string =>
    d.estado === 'CERRADO' ? 'Cerrado' : d.estado === 'ABIERTO' ? 'Abierto' : d.estado === 'FERIADO' ? 'Feriado' : d.estado === 'NO_HABIL' ? 'No hábil' : 'Sin cierre';
  claseEstado = (d: any): string =>
    d.estado === 'CERRADO' ? 'kb-badge-exito' : d.estado === 'ABIERTO' ? 'kb-badge-marca' : 'kb-badge-neutro';
  /** El punto de estado de la versión compacta (390 px). */
  clasePunto = (d: any): string =>
    d.estado === 'CERRADO' ? 'bg-emerald-600' : d.estado === 'ABIERTO' ? 'bg-primary' : 'bg-muted-foreground/40';
  atenuado = (d: any): boolean => d.estado === 'NO_HABIL' || d.estado === 'FERIADO';
  tieneNeto = (d: any): boolean => (d.estado === 'CERRADO' || d.estado === 'ABIERTO') && (Number(d.neto_usd) || 0) !== 0;

  /** Neto abreviado para la celda compacta (390 px, ~6 caracteres): sin la moneda, que va en la leyenda («86k», «1,2M»). Bs si hay tasa. */
  abreviado = (d: any): string => {
    const bs = d.neto_bs !== null && d.neto_bs !== undefined;
    const n = Number(bs ? d.neto_bs : d.neto_usd) || 0;
    const a = Math.abs(n);
    const t = a >= 1e6 ? `${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace('.', ',')}M` : a >= 1e3 ? `${(a / 1e3).toFixed(a >= 1e4 ? 0 : 1).replace('.', ',')}k` : a.toFixed(0);
    return `${n < 0 ? '-' : ''}${t}`;
  };

  etiqueta = (d: any): string =>
    `${+d.dia.slice(8, 10)} de ${MESES[+d.dia.slice(5, 7) - 1]}: ${this.textoEstado(d)}${d.feriado ? ' (' + d.feriado + ')' : ''}${d.es_hoy ? ', hoy' : ''}`;
}
